/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Box, styled, Typography } from "@mui/material";
import React from "react";

import Tabs, { TabPanel } from "../../../../components/Tabs";
import TestIds from "../../../../testIds";
import { ProductDetailsApiResponse } from "../../storage";

import { LongDescription } from "./LongDescription/LongDescription";

export const TabContainer = styled("div")(({ theme }) => ({
  width: "668px",
  [theme.breakpoints.down("lg")]: {
    width: "100%",
  },
}));

interface ProductDetailsTabsProps {
  item: ProductDetailsApiResponse;
}
export const ProductDetailsTabs = ({ item }: ProductDetailsTabsProps) => {
  const [tab, setTab] = React.useState(0);
  const overview = React.useMemo(() => {
    return (
      <Box data-testid={TestIds.feat.product.details.longDescription}>
        <LongDescription html={item.onlineLongDescription} />
      </Box>
    );
  }, [item.onlineLongDescription]);

  const details = React.useMemo(() => {
    type DetailItem = {
      name: string;
      description: string;
    };

    const detailItems: DetailItem[] = [];
    if (item.brand) {
      detailItems.push({ name: "Brand", description: item.brand });
    }
    if (item.onlineShortDescription) {
      detailItems.push({ name: "Description", description: item.onlineShortDescription });
    }
    if (item.name) {
      detailItems.push({ name: "Product name", description: item.name });
    }
    if (item.itemNumber) {
      detailItems.push({ name: "SKU (Item number)", description: item.itemNumber });
    }

    return (
      <Box display="flex" flexDirection="column" gap={"12px"}>
        {detailItems.map((x) => (
          <Box
            display="flex"
            flexDirection="column"
            key={x.name}
            data-testid={TestIds.feat.product.details.itemInformation}
            data-id={`${TestIds.feat.product.details.itemInformation}-${x.name}`}
          >
            <Typography variant="body2" fontWeight={"bold"} component={"span"}>
              {x.name}
            </Typography>
            <Typography variant="body2" component={"span"}>
              {x.description}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }, [item]);

  const tabsInfo = () => {
    const arr = [];
    if (item.onlineLongDescription) {
      arr.push({
        label: "Overview",
        "data-testid": TestIds.feat.product.details.tabOverview,
        details: overview,
        default: true,
      });
    }

    arr.push({
      label: "Product details",
      "data-testid": TestIds.feat.product.details.tabProductDetails,
      details: details,
      default: false,
    });
    return arr;
  };

  return (
    <TabContainer>
      <Tabs
        value={tab}
        setValue={setTab}
        tabsInfo={tabsInfo()}
        aria-label="product details tabs"
        id="product-details"
      />
      {tabsInfo().map((x, index) => (
        <TabPanel key={`${x.label}-${tab}`} value={tab} index={index} id={"product-details"}>
          {x.details}
        </TabPanel>
      ))}
    </TabContainer>
  );
};
